import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

const RADIUS = 9;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface RingSegment {
  color: string;
  /** Share of the ring; any unit, the segments are normalised over their sum. */
  value: number;
}

interface RenderedSegment {
  /** null on the single-value ring: no attribute, the stylesheet's progress colour applies. */
  color:     string | null;
  dashArray: string;
  offset:    number;
}

/**
 * "80% Drilled" chip with a small progress ring.
 * Two ways to fill the ring:
 *   `percent`  one arc, the default progress colour (Drilled / Loaded chips of the State view);
 *   `segments` several arcs in their own colours, clockwise from twelve o'clock (bucket splits
 *              of the Quality view). Normalised over their sum.
 * `label` is optional: without one the chip is the ring alone and `title` carries the wording.
 * It is prefixed with the percentage in `percent` mode only — a `segments` ring is a split,
 * not a single figure.
 * Optionally clickable (`clickable`): behaves like a toggle button and emits `pressed`.
 * Design source : Figma 2544:670423 ("Frame 39165")
 */
@Component({
  selector:        'app-progress-chip',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls:       ['./progress-chip.component.scss'],
  template: `
    <div class="chip"
      [class.chip--active]="active()"
      [class.chip--ring-only]="!label()"
      [class.chip--clickable]="clickable()"
      [title]="title()"
      [attr.role]="clickable() ? 'button' : null"
      [attr.tabindex]="clickable() ? 0 : null"
      [attr.aria-pressed]="clickable() ? active() : null"
      (click)="onActivate()"
      (keydown.enter)="onActivate()"
      (keydown.space)="onActivate(); $event.preventDefault()">
      <svg class="chip__ring" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="chip__track" cx="12" cy="12" [attr.r]="radius" />
        @for (arc of arcs(); track $index) {
          <circle class="chip__value" cx="12" cy="12" [attr.r]="radius"
            [class.chip__value--progress]="!arc.color"
            [attr.stroke]="arc.color"
            [attr.stroke-dasharray]="arc.dashArray"
            [attr.stroke-dashoffset]="arc.offset" transform="rotate(-90 12 12)" />
        }
      </svg>
      @if (text()) {
        <span class="chip__label">{{ text() }}</span>
      }
    </div>
  `,
})
export class ProgressChipComponent {
  /** 0 – 100; ignored when `segments` is set. */
  readonly percent   = input<number>(0);
  /** Several coloured arcs instead of the single `percent` one. */
  readonly segments  = input<readonly RingSegment[] | null>(null);
  /** Chip text after the percentage; without it the chip is the ring alone. */
  readonly label     = input<string>('');
  /** Tooltip and accessible name — the only wording of a chip without a `label`. */
  readonly title     = input<string>('');
  readonly active    = input<boolean>(false);
  readonly clickable = input<boolean>(false);

  /** Emitted on click / Enter / Space when `clickable`. */
  readonly pressed = output<void>();

  protected readonly radius = RADIUS;

  /** Chip text: "80% Drilled" on a `percent` ring, the bare label on a `segments` one. */
  protected readonly text = computed(() => {
    const label = this.label();
    if (!label) return '';
    return this.segments() ? label : `${this.percent()}% ${label}`;
  });

  /** Arcs of the ring, laid end to end clockwise from twelve o'clock. */
  protected readonly arcs = computed<RenderedSegment[]>(() => {
    const segments = this.segments()?.filter(segment => segment.value > 0);
    const shares: readonly { color: string | null; value: number }[] = segments?.length
      ? segments
      : [{ color: null, value: Math.min(100, Math.max(0, this.percent())) }];
    const total = segments?.length
      ? segments.reduce((sum, segment) => sum + segment.value, 0)
      : 100;
    if (!total) return [];

    let start = 0;
    return shares.map(share => {
      const length = (share.value / total) * CIRCUMFERENCE;
      const arc: RenderedSegment = {
        color:     share.color,
        dashArray: `${length.toFixed(2)} ${(CIRCUMFERENCE - length).toFixed(2)}`,
        // A negative offset shifts the dash forward, i.e. to the end of the previous arc
        offset:    -Number(start.toFixed(2)),
      };
      start += length;
      return arc;
    });
  });

  protected onActivate(): void {
    if (this.clickable()) this.pressed.emit();
  }
}
